// src/context/SettingsContext.tsx

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../utils/supabase';
import { networkUtils } from '../utils/networkUtils';
import { useToast } from './ToastContext';
import { useLocation } from 'react-router-dom';
import { createLogger } from '../utils/logger';
import { authCache } from '../utils/authCache';

const logger = createLogger({ module: 'Settings', level: 'info' });

interface Settings {
  defaultCareerCardsView: 'grid' | 'kanban';
  files_view_mode: string;
  sidebarCollapsed: boolean;
  darkMode: boolean;
  timezone: string;
  dashboard_tour_completed?: boolean;
}

interface DatabaseUserSettings {
  user_id: string;
  settings: Settings;
  files_view_mode: string;
}

interface PreloadedSettings {
  settings?: Settings | null;
  unified_home_settings?: {
    viewMode?: string;
    sortBy?: string;
    groupByType?: boolean;
    itemsPerPage?: number;
    scrollMode?: 'pagination' | 'infinite';
    [key: string]: unknown;
  } | null;
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => Promise<void>;
  isOffline: boolean;
  preloadUserSettings: (email: string, preloadedData?: PreloadedSettings) => Promise<void>;
}

const defaultSettings: Settings = {
  defaultCareerCardsView: 'grid',
  sidebarCollapsed: false,
  files_view_mode: 'grid',
  darkMode: false,
  timezone: 'UTC',
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const PUBLIC_PATHS = ['/', '/login', '/terms', '/privacy', '/gamification-support'];

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() => {
    logger.debug('Initializing settings with defaults:', defaultSettings);
    return defaultSettings;
  });
  const [isOffline, setIsOffline] = useState(networkUtils.isOffline());
  const { showToast } = useToast();
  const location = useLocation();

  // Add user state to track changes
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  
  // Add loading state to prevent multiple fetch operations
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  
  // Use a ref to track if settings have been fetched
  const settingsFetchedRef = useRef(false);

  // Add ref to track fetch retry count for stability
  const fetchRetryCountRef = useRef(0);
  const sessionStabilityRetryCountRef = useRef(0); // Separate counter for session stability
  const maxRetries = 3;
  const retryDelayMs = 1000; // 1 second between retries
  
  // Add ref for fetch deduplication
  const fetchPromiseRef = useRef<Promise<void> | null>(null);

  // Track user changes
  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        // Clear settings when user signs out
        setSettings(defaultSettings);
        settingsFetchedRef.current = false;
        fetchRetryCountRef.current = 0; // Reset retry count
        sessionStabilityRetryCountRef.current = 0; // Reset session stability retry count
        fetchPromiseRef.current = null; // Clear any pending fetch promise
      }
      
      logger.debug('Auth state changed:', { event, userId: session?.user?.id });
      setCurrentUser(session?.user?.id || null);
    });

    // Initial check - use authCache for faster startup
    const checkInitialUser = async () => {
      // Try cached user first for faster UI
      const cachedUser = await authCache.getUser();
      if (cachedUser) {
        logger.debug('Using cached user for settings initialization:', { userId: cachedUser.id });
        setCurrentUser(cachedUser.id);
      } else {
        // Fallback to direct call if cache is empty
        const { data: { user }, error } = await supabase.auth.getUser();
        if (!error) {
          setCurrentUser(user?.id || null);
        } else if (error.name !== 'AuthSessionMissingError') {
          // Only log non-expected errors
          logger.error('Failed to get user in SettingsContext', error);
        }
      }
    };
    checkInitialUser();

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Consolidate the preloadUserSettings function using useCallback
  const preloadUserSettings = useCallback(async (email: string, preloadedData?: PreloadedSettings) => {
    if (!email) return;

    try {
      let settingsData;
      
      if (preloadedData) {
        // Use the preloaded data if available
        settingsData = {
          settings: preloadedData.settings || defaultSettings,
          unified_home_settings: preloadedData.unified_home_settings || null
        };
      } else {
        // Fallback to fetching from Supabase if no preloaded data
        const { data, error } = await supabase
          .from('user_settings')
          .select('settings, unified_home_settings')
          .eq('email', email.toLowerCase())
          .single();

        if (error) {
          logger.error('Error preloading user settings:', undefined, {
            error: (error && typeof error === 'object' && 'message' in error) 
              ? String(error.message) 
              : String(error || 'Unknown error'),
            errorCode: (error && typeof error === 'object' && 'code' in error) 
              ? String(error.code) 
              : 'unknown',
            errorDetails: (error && typeof error === 'object' && 'details' in error) 
              ? String(error.details) 
              : 'none',
            email: email.toLowerCase()
          });
          return;
        }

        settingsData = data;
      }

      // Store the settings in localStorage for immediate access
      if (settingsData?.settings) {
        localStorage.setItem(`user_settings_${email}`, JSON.stringify(settingsData.settings));
        
        // Update the state with preloaded settings, but only if no current user
        // (to avoid conflicts when already logged in)
        if (!currentUser) {
          setSettings(prev => ({
            ...prev,
            ...settingsData.settings
          }));
        }
      }
      
      if (settingsData?.unified_home_settings) {
        localStorage.setItem(`unified_home_settings_${email}`, JSON.stringify(settingsData.unified_home_settings));
      }

      logger.debug('Preloaded user settings:', {
        email,
        hasSettings: !!settingsData?.settings,
        hasUnifiedSettings: !!settingsData?.unified_home_settings
      });
    } catch (error) {
      logger.error('Error in preloadUserSettings:', undefined, {
        error: error instanceof Error ? error.message : String(error),
        errorName: error instanceof Error ? error.name : 'Unknown',
        email
      });
    }
  }, [currentUser]);

  // Fetch settings once when user changes
  useEffect(() => {
    const fetchSettings = async () => {
      // Skip if no user is set
      if (!currentUser) return;
      
      // Return existing promise if already fetching (deduplication)
      if (fetchPromiseRef.current) {
        logger.debug('Fetch already in progress, returning existing promise');
        return fetchPromiseRef.current;
      }
      
      // Skip if already loading settings
      if (isLoadingSettings) return;
      
      // Skip if settings already fetched and not a new user
      if (settingsFetchedRef.current) {
        logger.debug('Settings already fetched for user, skipping redundant fetch');
        return;
      }
      
      // Start the loading state before creating the promise
      setIsLoadingSettings(true);
      
      // Create new fetch promise
      fetchPromiseRef.current = (async () => {
        try {
          logger.debug('Starting new settings fetch', { userId: currentUser });
          
          // Reset retry counts for new fetch
          fetchRetryCountRef.current = 0;
          sessionStabilityRetryCountRef.current = 0;
          
          // Add a small delay for new users to allow network to stabilize after login
          const isNewSession = !settingsFetchedRef.current;
          if (isNewSession) {
            logger.debug('New session detected, adding stabilization delay');
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

          // Check session to ensure auth is stable before fetching settings
          const { data: sessionData } = await supabase.auth.getSession();
          if (!sessionData.session || sessionData.session.user.id !== currentUser) {
            // Check if we've exceeded session stability retries
            if (sessionStabilityRetryCountRef.current >= maxRetries) {
              logger.warn('Max session stability retries reached, aborting fetch');
              throw new Error('Session not stable after maximum retries');
            }
            
            sessionStabilityRetryCountRef.current += 1;
            logger.debug(`Session not yet stable, retry ${sessionStabilityRetryCountRef.current}/${maxRetries}`);
            
            // Schedule retry with exponential backoff
            const retryDelay = retryDelayMs * Math.pow(2, sessionStabilityRetryCountRef.current - 1);
            setTimeout(() => {
              if (currentUser) {
                fetchPromiseRef.current = null; // Clear promise to allow retry
                fetchSettings();
              }
            }, retryDelay);
            return;
          }
      
          // Check network connectivity before making the request
          if (!navigator.onLine) {
            logger.warn('Device is offline, skipping settings fetch');
            
            // Listen for when we come back online
            const handleOnline = () => {
              window.removeEventListener('online', handleOnline);
              logger.info('Device back online, retrying settings fetch');
              fetchPromiseRef.current = null; // Clear promise to allow retry
              fetchSettings();
            };
            window.addEventListener('online', handleOnline);
            throw new Error('Device is offline');
          }
      
          logger.debug('=== Starting fetchSettings ===', {
            currentSettings: settings,
            userId: currentUser,
            event: 'user changed',
            retryCount: fetchRetryCountRef.current
          });
          
          const { data, error } = await supabase
            .from('user_settings')
            .select('*')
            .eq('user_id', currentUser)
            .single();

          if (error) {
            // Check if this is a network error and should be retried
            const isNetworkError = error.message?.includes('Failed to connect') || 
                                  error.message?.includes('NETWORK_ERROR') ||
                                  error.message?.includes('fetch');
            
            if (isNetworkError && fetchRetryCountRef.current < maxRetries) {
              fetchRetryCountRef.current += 1;
              logger.warn(`Network error fetching settings, retrying (attempt ${fetchRetryCountRef.current}/${maxRetries})`, {
                error: error.message || error.toString(),
                userId: currentUser
              });
              
              // Wait longer for network errors (exponential backoff)
              const networkRetryDelay = retryDelayMs * Math.pow(2, fetchRetryCountRef.current - 1);
              setTimeout(() => {
                fetchPromiseRef.current = null; // Clear promise to allow retry
                fetchSettings();
              }, networkRetryDelay);
              return;
            }
            
            if (error.code === 'PGRST116') {
              logger.debug('No settings found during fetch, creating defaults', { 
                userId: currentUser,
                defaults: defaultSettings 
              });
              await createDefaultSettings(currentUser);
              
              // After creating defaults, check if we should retry the fetch
              if (fetchRetryCountRef.current < maxRetries) {
                fetchRetryCountRef.current += 1;
                logger.debug(`Settings created, retrying fetch (attempt ${fetchRetryCountRef.current}/${maxRetries})`);
                
                // Wait a bit before retrying to allow the database to settle
                setTimeout(() => {
                  fetchPromiseRef.current = null; // Clear promise to allow retry
                  fetchSettings();
                }, retryDelayMs);
                return; // Exit early, we'll call again after delay
              } else {
                // After max retries, just use defaults
                logger.warn(`Max retries (${maxRetries}) reached, using default settings`);
                setSettings(defaultSettings);
              }
            } else {
              logger.error('Error fetching settings:', undefined, { 
                error: (error && typeof error === 'object' && 'message' in error) 
                  ? String(error.message) 
                  : String(error || 'Unknown error'), 
                errorCode: (error && typeof error === 'object' && 'code' in error) 
                  ? String(error.code) 
                  : 'unknown',
                errorDetails: (error && typeof error === 'object' && 'details' in error) 
                  ? String(error.details) 
                  : 'none',
                userId: currentUser 
              });
            }
          } else if (data) {
            const userSettings = data as DatabaseUserSettings;
            const newSettings = {
              ...defaultSettings,
              ...userSettings.settings,
              files_view_mode: userSettings.files_view_mode
            };
            logger.debug('Settings fetched successfully:', {
              current: settings,
              incoming: userSettings.settings,
              merged: newSettings,
              userId: currentUser
            });
            setSettings(newSettings);
            
            // Store the dark mode setting in localStorage for immediate access
            if (typeof newSettings.darkMode === 'boolean') {
              localStorage.setItem('careercard_dark_mode', String(newSettings.darkMode));
              logger.debug('Updated localStorage with darkMode setting', { darkMode: newSettings.darkMode });
            }
            
            // Reset retry counter on success
            fetchRetryCountRef.current = 0;
          }
          
          // Mark settings as fetched
          settingsFetchedRef.current = true;
        } catch (err) {
          logger.error('Unexpected error in fetchSettings:', undefined, {
            error: err instanceof Error ? err.message : String(err),
            errorName: err instanceof Error ? err.name : 'Unknown',
            errorStack: err instanceof Error ? err.stack : undefined,
            userId: currentUser
          });
          throw err; // Re-throw to be caught by outer try-catch
        }
        
        logger.debug('=== Completing fetchSettings ===');
      })();
      
      // Handle the promise
      try {
        await fetchPromiseRef.current;
      } catch (error) {
        logger.error('Settings fetch failed:', undefined, {
          error: error instanceof Error ? error.message : String(error)
        });
      } finally {
        // Only reset loading state when completely done
        setIsLoadingSettings(false);
        fetchPromiseRef.current = null;
      }
    };

    fetchSettings();
  }, [currentUser]);

  useEffect(() => {
    networkUtils.init();
    return networkUtils.onConnectivityChange((offline) => {
      setIsOffline(offline);
      // Only show toast messages if not on a public page
      if (!PUBLIC_PATHS.includes(location.pathname)) {
        if (offline) {
          showToast('You are currently offline. Some features may be unavailable.', 'info');
        } else {
          showToast('Back online!', 'success');
        }
      }
    });
  }, [showToast, location.pathname]);

  const createDefaultSettings = async (userId: string) => {
    const defaultUserSettings: DatabaseUserSettings = {
      user_id: userId,
      settings: defaultSettings,
      files_view_mode: defaultSettings.files_view_mode
    };

    const { error } = await supabase
      .from('user_settings')
      .insert(defaultUserSettings);

    if (error) {
      logger.error('Error creating default settings:', undefined, {
        error: (error && typeof error === 'object' && 'message' in error) 
          ? String(error.message) 
          : String(error || 'Unknown error'),
        errorCode: (error && typeof error === 'object' && 'code' in error) 
          ? String(error.code) 
          : 'unknown',
        errorDetails: (error && typeof error === 'object' && 'details' in error) 
          ? String(error.details) 
          : 'none',
        userId
      });
    }
  };

  // Optimize the updateSettings function with useCallback to prevent recreating it on every render
  const updateSettings = useCallback(async (newSettings: Partial<Settings>) => {
    // Exit early if no user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    setIsLoadingSettings(true);
    
    try {
      // Update state first for responsive UI using functional update
      setSettings(prevSettings => {
        const mergedSettings = {
          ...prevSettings,
          ...newSettings
        };
        return mergedSettings;
      });
      
      // Get existing settings from database
      const { data: existingData, error: fetchError } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      // Get the current merged settings from state
      const currentSettings = await new Promise<Settings>((resolve) => {
        setSettings(prev => {
          resolve(prev);
          return prev;
        });
      });

      // Prepare update data
      const updateData: { user_id: string; settings: Settings; files_view_mode: string } = {
        user_id: user.id,
        settings: currentSettings,
        files_view_mode: ('files_view_mode' in newSettings && typeof newSettings.files_view_mode === 'string')
          ? newSettings.files_view_mode
          : existingData?.files_view_mode || defaultSettings.files_view_mode
      };

      // Update database
      const { error: updateError } = await supabase
        .from('user_settings')
        .upsert(updateData, {
          onConflict: 'user_id',
          ignoreDuplicates: false
        });

      if (updateError) throw updateError;
      
      // Update localStorage for dark mode to ensure immediate availability on next load
      if ('darkMode' in newSettings && typeof currentSettings.darkMode === 'boolean') {
        localStorage.setItem('careercard_dark_mode', String(currentSettings.darkMode));
      }
      
      logger.debug('Settings updated successfully:', { 
        userId: user.id,
        newSettings: currentSettings 
      });
    } catch (error) {
      logger.error('Error updating settings:', undefined, {
        error: (error && typeof error === 'object' && 'message' in error) 
          ? String(error.message) 
          : String(error || 'Unknown error'),
        errorName: error instanceof Error ? error.name : 'Unknown',
        errorCode: (error && typeof error === 'object' && 'code' in error) 
          ? String(error.code) 
          : 'unknown',
        userId: user?.id
      });
      // Revert to previous settings if update fails
      const { data: currentData } = await supabase
        .from('user_settings')
        .select('settings')
        .eq('user_id', user.id)
        .single();
        
      if (currentData?.settings) {
        setSettings({
          ...defaultSettings,
          ...currentData.settings
        });
      }
      
      throw error;
    } finally {
      setIsLoadingSettings(false);
    }
  }, []);

  // Use useMemo to prevent unnecessary re-creation of the context value
  const value = useMemo(() => ({
    settings,
    updateSettings,
    isOffline,
    preloadUserSettings,
  }), [settings, updateSettings, isOffline, preloadUserSettings]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};